import AiStarIcon from "@redotech/redo-icons/ai-star-orange.svg";
import XIcon from "@redotech/redo-icons/x-close.svg";
import { aiSubjectLineTones } from "@redotech/redo-marketing-core-common/ai-subject-line-tones";
import { Flex } from "@redotech/redo-web/flex";
import { Text } from "@redotech/redo-web/text";
import { memo, useCallback, useEffect, useRef, useState } from "react";
import * as styles from "./ai-suggestions.module.css";
import { AiSuggestion, useAiSuggestions } from "./use-ai-suggestions";

interface AiEmailSuggestionsProps {
  fetchSuggestions: (
    params: { text: string; tone?: string },
    options: { signal: AbortSignal },
  ) => Promise<AiSuggestion[]>;
  logLabel: string;
  onSuggestionClick: (suggestion: string) => void;
  text: string;
  title: string;
}

export const AiEmailSuggestions = memo(function AiEmailSuggestions(
  props: AiEmailSuggestionsProps,
) {
  const { fetchSuggestions, logLabel, onSuggestionClick, text, title } = props;

  const [selectedTone, setSelectedTone] = useState<string | null>(null);
  const [scrollPercentage, setScrollPercentage] = useState(0);
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const toneButtonRefs = useRef<{ [key: string]: HTMLDivElement | null }>({});

  const {
    suggestions: aiSuggestions,
    isLoading,
    isOpen: shouldShow,
    regenerate,
    accept,
    dismiss,
  } = useAiSuggestions({
    text,
    tone: selectedTone,
    load: (value, tone, signal) =>
      fetchSuggestions({ text: value, tone }, { signal }),
    logLabel,
  });

  const handleScroll = () => {
    const container = scrollContainerRef.current;
    if (container) {
      const scrollLeft = container.scrollLeft;
      const maxScroll = container.scrollWidth - container.clientWidth;
      const percentage = maxScroll > 0 ? scrollLeft / maxScroll : 0;
      setScrollPercentage(percentage);
    }
  };

  const scrollToSelectedTone = useCallback(() => {
    if (!selectedTone || !scrollContainerRef.current) {
      return;
    }

    const selectedButton = toneButtonRefs.current[selectedTone];
    if (selectedButton) {
      selectedButton.scrollIntoView({
        behavior: "smooth",
        block: "nearest",
        inline: "center",
      });
    }
  }, [selectedTone]);

  const handleScrollbarDrag = (e: React.MouseEvent<HTMLDivElement>) => {
    const scrollbar = e.currentTarget;
    const container = scrollContainerRef.current;
    if (!container) {
      return;
    }

    const startX = e.clientX;
    const thumbWidth = 38;
    const maxThumbLeft = scrollbar.clientWidth - thumbWidth;
    const startThumbLeft = scrollPercentage * maxThumbLeft;

    const handleMouseMove = (moveEvent: MouseEvent) => {
      const deltaX = moveEvent.clientX - startX;
      const newThumbLeft = Math.max(
        0,
        Math.min(maxThumbLeft, startThumbLeft + deltaX),
      );
      const newPercentage = newThumbLeft / maxThumbLeft;
      const maxScroll = container.scrollWidth - container.clientWidth;
      container.scrollLeft = newPercentage * maxScroll;
    };

    const handleMouseUp = () => {
      document.removeEventListener("mousemove", handleMouseMove);
      document.removeEventListener("mouseup", handleMouseUp);
    };

    document.addEventListener("mousemove", handleMouseMove);
    document.addEventListener("mouseup", handleMouseUp);
  };

  useEffect(() => {
    if (shouldShow && !isLoading) {
      setTimeout(() => {
        scrollToSelectedTone();
      }, 100);
    }
  }, [selectedTone, shouldShow, isLoading, scrollToSelectedTone]);

  if (!shouldShow) {
    return null;
  }

  const hasPaddingBottom = isLoading || aiSuggestions.length === 0;

  return (
    <div
      className={styles.container}
      onMouseDown={(event) => {
        // Keep the textarea focused: leaving the field cancels suggestions.
        event.preventDefault();
      }}
    >
      <Flex
        className={`${styles.suggestionsPanel} ${hasPaddingBottom ? styles.hasPaddingBottom : ""}`}
        dir="column"
        gap="sm"
        p="none"
      >
        <Flex dir="column" gap="lg" w="full">
          <Flex
            align="center"
            dir="row"
            justify="space-between"
            pl="md"
            pr="md"
            w="full"
          >
            <Flex align="center" gap="sm">
              <AiStarIcon
                className={`${styles.aiStarIcon} ${isLoading ? styles.spinning : ""}`}
              />
              <Text fontSize="xs" textColor="primary">
                {isLoading ? "Generating suggestions..." : title}
              </Text>
            </Flex>
            {/* design-allow: non-focusable; a button would blur the textarea */}
            <div className={styles.dismissButton} onClick={dismiss}>
              <XIcon height="14" width="14" />
            </div>
          </Flex>
          {!isLoading && (
            <>
              <div
                className={styles.tonesContainer}
                onScroll={handleScroll}
                ref={scrollContainerRef}
              >
                {aiSubjectLineTones.map((tone) => {
                  const isSelected = selectedTone === tone;
                  return (
                    // design-allow: non-focusable; a button would blur the textarea
                    <div
                      className={`${styles.tonePill} ${isSelected ? styles.selected : styles.unselected}`}
                      key={tone}
                      onClick={() => {
                        const newTone = selectedTone === tone ? null : tone;
                        setSelectedTone(newTone);
                        regenerate(newTone);
                      }}
                      ref={(el) => {
                        toneButtonRefs.current[tone] = el;
                      }}
                    >
                      {tone}
                    </div>
                  );
                })}
              </div>
              <div
                className={styles.scrollbarTrack}
                onMouseDown={handleScrollbarDrag}
              >
                <div
                  className={styles.scrollbarThumb}
                  style={{
                    left: scrollContainerRef.current?.clientWidth
                      ? `${scrollPercentage * (100 - (40 / scrollContainerRef.current.clientWidth) * 100)}%`
                      : "0%",
                  }}
                />
              </div>
            </>
          )}
          {!isLoading && aiSuggestions.length > 0 && (
            <Flex dir="column" gap="md" w="full">
              {aiSuggestions.map((suggestion, index) => {
                const isLastItem = index === aiSuggestions.length - 1;
                return (
                  <div key={index}>
                    <Flex
                      align="center"
                      className={`${styles.suggestionItem} ${isLastItem ? styles.last : ""}`}
                      justify="space-between"
                      onClick={() => {
                        accept(suggestion.text);
                        onSuggestionClick(suggestion.text);
                      }}
                      w="full"
                    >
                      <Text fontSize="xs" px="md" py="xs" textColor="primary">
                        {suggestion.text}
                      </Text>
                    </Flex>
                  </div>
                );
              })}
            </Flex>
          )}
        </Flex>
      </Flex>
    </div>
  );
});
